import {useCallback, useEffect, useId, useRef, useState} from 'react'

export interface CloudContinueControlProps {
  enabled: boolean
  onChange: (enabled: boolean) => void
  disabled?: boolean
}

/**
 * The chat header's "continue in the cloud" setting. The header button only opens a small panel;
 * the setting itself is an explicit on/off switch with a sentence saying what it does, because a
 * bare icon that toggles on click gave users no way to tell what they had switched. A dot on the
 * button shows the setting is on while the panel is closed.
 */
export const CloudContinueControl = ({
  enabled,
  onChange,
  disabled = false,
}: CloudContinueControlProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const switchRef = useRef<HTMLInputElement>(null)
  const switchId = useId()
  const descriptionId = useId()

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false)
    if (restoreFocus) toggleRef.current?.focus()
  }, [])

  useEffect(() => {
    if (open) switchRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) close(false)
    }
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEscape)
    }
  }, [open, close])

  // A conversation can go away (or not exist yet) while the panel is open.
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  return (
    <div className='ai-chat-v2-sidebar__dropdown' ref={dropdownRef}>
      <button
        ref={toggleRef}
        type='button'
        className={`ai-chat-v2-sidebar__icon-btn ai-chat-v2-sidebar__cloud-continue-btn${
          open ? ' active' : ''
        }`}
        data-testid='omics-ai-chat-cloud-continue-btn'
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={enabled ? 'Continue in the cloud: on' : 'Continue in the cloud: off'}
        aria-label='Continue in the cloud'
        aria-haspopup='dialog'
        aria-expanded={open}
      >
        {/* Solid only: this build ships fontawesome-free, whose `far` subset has no fa-cloud. */}
        <i className='fas fa-cloud' />
        {enabled ? (
          <span
            className='ai-chat-v2-sidebar__cloud-continue-dot'
            data-testid='omics-ai-chat-cloud-continue-on'
            aria-hidden='true'
          />
        ) : null}
      </button>
      {open && (
        <div
          className='ai-chat-v2-sidebar__dropdown-menu ai-chat-v2-sidebar__cloud-continue-panel'
          role='dialog'
          aria-label='Continue in the cloud'
        >
          <div className='form-check form-switch mb-0'>
            <input
              ref={switchRef}
              id={switchId}
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={enabled}
              aria-describedby={descriptionId}
              data-testid='omics-ai-chat-cloud-continue-switch'
              onChange={(e) => onChange(e.target.checked)}
            />
            <label className='form-check-label' htmlFor={switchId}>
              Continue in the cloud
            </label>
          </div>
          <div id={descriptionId}>
            <p className='ai-chat-v2-sidebar__cloud-continue-help'>
              When an analysis (a job, a download, etc.) started from this conversation finishes,
              the agent behind this chat reads the results and replies remotely, even if you have
              closed this tab or the browser. You will see the outcome when you come back.
            </p>
            <p className='ai-chat-v2-sidebar__cloud-continue-help'>
              If this option is switched off and you leave the chat, the agent stops once it has
              answered your last message.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
